import { Clipboard } from "@capacitor/clipboard";
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Share } from '@capacitor/share';
import {
    IonButton,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonToolbar,
    useIonToast,
} from '@ionic/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    alertCircleOutline,
    cloudUploadOutline,
    linkOutline,
    personOutline,
    sendOutline,
    sparklesOutline,
} from 'ionicons/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { Screens, useAnalytics } from '../../../AppAnalyticsProvider';
import { useAppState } from '../../../AppListenerProvider';
import { AppContext } from '../../../AppStateProvider';
import biskSvg from "../../../assets/icons/bisk/BISK_ICON_NO_DEGREE.svg";
import StickerEditor from '../../ComponentsSticker/StickerEditor/StickerEditor';
import { useInstagramShare } from '../../../hooks/useInstagramShare';
import { getAdjustedTiming, QueryTiming } from '../../../hooks/useQueryConfig';
import { BiskTransaction } from '../../../models/biskTransaction';
import { UserBisk } from '../../../models/userBisk';
import BiskService from '../../../services/bisk.service';
import TricksService from '../../../services/tricks.service';
import UserService from '../../../services/user.service';
import TrickVideoLoading from '../../ComponentsUI/TrickVideoLoading/TrickVideoLoading';
import HomePageVideoPlayer from '../../ComponentsVideo/HomePageVideo/HomePageVideo';
import { resolveClipAspectRatio } from './clipAspect';
import './UploadSuccess.css';

const customEmojis = ["👏", "🎉", "🎈", "🫡", "🎊", "😎", "😮‍💨", "😤"]

// Rotating share prompts so it doesn't say the same thing every upload.
const SHARE_LINES = [
    "Nice one — drop it on your story.",
    "Too clean to keep to yourself.",
    "Put it on your story before someone else does.",
    "Your followers need to see this.",
    "Certified heater — share it.",
    "Don't gatekeep that one.",
    "Story-worthy. Obviously.",
    "Show it off while it's fresh.",
    "Send it to the timeline.",
    "That one deserves an audience.",
];

// Stable per-clip pick (no flicker on re-render).
function pickShareLine(key: string): string {
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
    return SHARE_LINES[Math.abs(h) % SHARE_LINES.length];
}

// Bottom-row buttons — highlighted (visible), not greyed-out.
const SECONDARY_BTN_STYLE = {
    "--background": "rgba(255,255,255,0.16)",
    "--background-activated": "rgba(255,255,255,0.28)",
    "--background-hover": "rgba(255,255,255,0.22)",
    "--color": "#ffffff",
    "--border-radius": "12px",
    "--box-shadow": "none",
} as React.CSSProperties;

interface UploadSuccessParams {
    user_id?: string;
    id?: string;
}

interface UploadSuccessProps extends RouteComponentProps<UploadSuccessParams> {
}

const UploadSuccess: React.FC<UploadSuccessProps> = ({ match }) => {
    const { state, dispatch } = React.useContext(AppContext);
    const { user_id, id } = match.params;
    const history = useHistory();
    const { isNative, platform } = useAppState();
    const { trackEvent, trackScreen } = useAnalytics();

    const { instagramInstalled } = useInstagramShare();

    const [isClaimingBisk, setIsClaimingBisk] = useState<boolean>(false);
    const [isStickerPickerOpen, setIsStickerPickerOpen] = useState<boolean>(false);

    const [present] = useIonToast();
    const [claimBisk, setClaimBisk] = useState<boolean>(false);
    const [biskDisabled, setBiskDisabled] = useState<boolean>(false);
    const [biskReward, setBiskReward] = useState<number>(0);
    const [biskMessage, setBiskMessage] = useState<string>('');

    const queryClient = useQueryClient();

    const { data: trick, isLoading: isTrickLoading } = useQuery({
        queryKey: ['userTrick', id, user_id],
        queryFn: () => TricksService.getUserTrick(id, user_id),
        // For daily updates
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
    });

    const { data: videoProperties, isLoading: isLoadingVideoProperties } = useQuery({
        queryKey: ['videoProperties', id],
        queryFn: () => TricksService.getTrickVideoProperties(id),
        enabled: !!trick,
        // For daily updates
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
    });

    const getBiskReward = async () => {
        const biskRewardObject = await UserService.getBiskRewardForUpload(user_id);
        if (biskRewardObject) {
            console.log("Bisk Reward Object: ", biskRewardObject);
            const bisk = biskRewardObject['reward'];
            const message = biskRewardObject['message'];
            if (bisk > 0) {
                setClaimBisk(true);
                setBiskReward(bisk);
                setBiskMessage(message);
            } else {
                setClaimBisk(false);
            }
        } else {
            setClaimBisk(false);
        }
    }

    useEffect(() => {
        trackScreen(Screens.UPLOAD_SUCCESS, 'UploadSuccessScreen');
        trackEvent('upload_success_viewed', {
            trick_id: id,
            user_id: user_id,
            is_native: isNative,
            platform: platform
        });
    }, []);

    useEffect(() => {
        getBiskReward();
    }, [user_id]);

    const invalidateQueries = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['userProfile', state.user.username] }),
            queryClient.invalidateQueries({ queryKey: ['userTricks', state.user.id] }),
            queryClient.invalidateQueries({ queryKey: ['streak', state.user.id] }),
            queryClient.invalidateQueries({ queryKey: ['userStatsRaw', state.user.id] }),
            queryClient.invalidateQueries({ queryKey: ['newTricks'] }),
            queryClient.invalidateQueries({ queryKey: ['eventTricks'] }),
        ]);
    }

    const handleGoToProfile = async () => {
        trackEvent('upload_success_view_profile', {
            trick_id: id,
            user_id: state.user.id
        });
        // Use router.push with a `replace` strategy to avoid the tab replacement issue.
        await Haptics.impact({ style: ImpactStyle.Medium });
        await invalidateQueries();
        history.push(`/profile/${state.user.username}`);
    }

    const uploadMore = async () => {
        trackEvent('upload_success_upload_more', {
            trick_id: id,
            user_id: user_id
        });
        await Haptics.impact({ style: ImpactStyle.Medium });
        await invalidateQueries();
        history.push('/upload');
    }

    const handleShare = async () => {
        trackEvent('upload_success_share', {
            trick_id: id,
            share_type: 'native',
            trick_name: trick?.trick_name
        });
        await Haptics.impact({ style: ImpactStyle.Light });
        try {
            await Share.share({
                title: trick.trick_name,
                text: `${trick.trick_name} // ${state.user?.username} // Ecliptic!`,
                url: `https://ecliptic.day/clip/${user_id}/${id}`,
                dialogTitle: 'Share your trick',
            });
        } catch (error) {
            trackEvent('upload_success_share_error', {
                error_code: 'share_failed',
                trick_id: id
            });
            console.error('Error sharing', error);
        }
    };

    const handleInstagramShare = async () => {
        trackEvent('upload_success_share_to_story', {
            trick_id: id,
            instagram_installed: instagramInstalled,
            platform: platform
        });
        await Haptics.impact({ style: ImpactStyle.Light });
        // Open Story Studio. It self-contains every destination (Instagram,
        // TikTok, Snap, Save, More); dismissing returns here to the success
        // screen, where the rider can keep editing or head back to upload again.
        setIsStickerPickerOpen(true);
    };

    const handleCopyLink = async () => {
        trackEvent('upload_success_copy_link', {
            trick_id: id,
            user_id: user_id
        });

        await Clipboard.write({
            string: `https://ecliptic.day/clip/${user_id}/${id}`
        });

        present({
            message: 'Link copied to clipboard!',
            duration: 3000,
            position: 'top',
            color: 'success',

        });
    }

    const getBisk = async (profileId: string) => {
        try {
            const bisk: UserBisk = await BiskService.getBiskForUser(profileId);
            return bisk.bisk;
        } catch (e) {
            console.log("Error getting Bisk: ", e);
            return state.bisk;
        }
    }

    const onRedeem = async (biskSend: number, note: string) => {
        try {
            const biskRewardTransaction: BiskTransaction = await UserService.postBiskRewardForUpload(user_id, biskSend, note);
            console.log("Result: ", biskRewardTransaction);
            await Haptics.impact({ style: ImpactStyle.Medium });
            await present({
                message: `Congrats on the ${biskRewardTransaction.amount} Bisk!`,
                duration: 5000, // Duration in milliseconds
                icon: biskSvg,
                position: "top",
                color: "success",
                buttons: [
                    {
                        text: 'Ok',
                        role: 'cancel'
                    }
                ]
            });
            const recieverBisk = await getBisk(user_id);
            dispatch({ type: "setBisk", payload: recieverBisk })
        } catch (e) {
            console.log("Error sending Bisk: ", e);
            await present({
                message: `Failed to redeem Bisk: Error: ${e}`,
                duration: 5000, // Duration in milliseconds
                icon: alertCircleOutline,
                position: "top",
                color: "danger",
                buttons: [
                    {
                        text: 'Ok',
                        role: 'cancel'
                    }
                ]
            });
        } finally {
            setBiskDisabled(true);
        }
    }

    const handleClaimBisk = async () => {
        if (isClaimingBisk) return; // Prevent double execution
        setIsClaimingBisk(true);
        try {
            trackEvent('upload_success_claim_bisk', {
                trick_id: id,
                bisk_amount: biskReward,
                bisk_message: biskMessage
            });
            await onRedeem(biskReward, biskMessage);
        } finally {
            setIsClaimingBisk(false);
        }
    };

    const randomEmoji = customEmojis[Math.floor(Math.random() * customEmojis.length)];
    const shareLine = useMemo(() => pickShareLine(id || user_id || trick?.trick_id || "x"), [id, user_id, trick?.trick_id]);

    // TODO: On Mobile - Claim Still Off - Not Disabled - Fix
    // Clicking on View Trick Messes Up Routing When Go Back
    // Need to Test Go To Profile Button

    if (isTrickLoading || isLoadingVideoProperties) {
        return (
            <IonPage>
                <IonHeader translucent={true}>
                    {/* Add a toolbar to maintain consistent height */}
                    <IonToolbar>
                    </IonToolbar>
                </IonHeader>
                <IonContent>
                    <TrickVideoLoading />
                </IonContent>
            </IonPage>
        );
    }

    // Width / height ratio for the clip frame: the backend decimal, otherwise an
    // orientation-based default. (videoProperties is guaranteed loaded here — the
    // loading guard above returns early.) The player fills this frame.
    const clipAspect = resolveClipAspectRatio(
        null,
        videoProperties.aspect_ratio,
        videoProperties.is_vertical,
    );

    return (
        <IonPage>
            <IonContent fullscreen scrollY={false} color="theme">
                <div className="relative w-full h-full overflow-hidden bg-black select-none">
                    {/* Blurred fill of the clip — fills the screen for ANY aspect
                        (portrait or landscape) without ever cropping the trick. */}
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: `url(${trick.thumbnail})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            filter: "blur(40px) saturate(1.3) brightness(0.45)",
                            transform: "scale(1.3)",
                        }}
                    />
                    {/* Scrim for legibility of the floating header + actions */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/95" />

                    {/* Content column over the backdrop: header / clip / actions */}
                    <div
                        className="relative z-10 flex flex-col h-full"
                        style={{
                            paddingTop: `calc(0.75rem + var(--ion-safe-area-top, 0))`,
                            paddingBottom: `calc(1rem + var(--ion-safe-area-bottom, 0))`,
                        }}
                    >
                        {/* Pride first — celebrate the clip they just landed */}
                        <div className="text-center px-6 shrink-0">
                            <h1 className="text-2xl font-bold text-white drop-shadow-lg">
                                {trick.trick_name} is live {randomEmoji}
                            </h1>
                            <p className="text-sm text-zinc-300 mt-1 drop-shadow">
                                {shareLine}
                            </p>
                        </div>

                        {/* The clip — the hero. Its frame hugs the real aspect ratio
                            (portrait, landscape, or square) so the trick is fully
                            visible with no dead space. Height-driven: it grows to fill
                            the space left between the title and the actions (capped at
                            62vh), and the width follows from the ratio — clamped to the
                            screen so landscape clips never overflow. Plays through the
                            standard AutoVideoPlayer so the rider gets the feed's controls
                            (incl. unmute) and can confirm their clip's sound — it respects
                            the global volume and force-mutes only while the Story Studio is
                            open over it. */}
                        <div className="flex-1 min-h-0 flex items-center justify-center px-3 py-3">
                            {!videoProperties.is_external ? (
                                <div
                                    className="upload-success-clip relative h-full w-auto max-h-[62vh] max-w-full rounded-2xl overflow-hidden shadow-2xl bg-black"
                                    style={{ aspectRatio: clipAspect }}
                                >
                                    <HomePageVideoPlayer
                                        isCapacitor={isNative}
                                        src={trick.video}
                                        thumbnail={trick.thumbnail}
                                        id={trick.trick_id}
                                        isCached={false}
                                        isHorizontal={!videoProperties.is_vertical}
                                        isExternal={videoProperties.is_external}
                                        aspectRatio={videoProperties.aspect_ratio}
                                        isAirtime={videoProperties.is_airtime}
                                        isVimeo={videoProperties.is_vimeo}
                                        isYoutube={videoProperties.is_youtube}
                                        isScrolling={false}
                                        scrollSpeed={0}
                                        forceMuted={isStickerPickerOpen}
                                        controlsInitiallyVisible={true}
                                    />
                                </div>
                            ) : (
                                <div className="w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl bg-black">
                                    <HomePageVideoPlayer
                                        isCapacitor={isNative}
                                        src={trick.video}
                                        thumbnail={trick.thumbnail}
                                        id={trick.trick_id}
                                        isCached={false}
                                        isHorizontal={!videoProperties.is_vertical}
                                        isExternal={videoProperties.is_external}
                                        aspectRatio={videoProperties.aspect_ratio}
                                        isAirtime={videoProperties.is_airtime}
                                        isVimeo={videoProperties.is_vimeo}
                                        isYoutube={videoProperties.is_youtube}
                                        isScrolling={false}
                                        scrollSpeed={0}
                                        forceMuted={isStickerPickerOpen}
                                        controlsInitiallyVisible={true}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Floating actions — generous spacing, share on top */}
                        <div className="px-4 shrink-0 flex flex-col gap-3">
                            {/* HERO — share to your story (white icon, electric button) */}
                            {isNative ? (
                                <IonButton
                                    className="neon-button"
                                    expand="block"
                                    onClick={async () => await handleInstagramShare()}
                                    disabled={videoProperties.is_external}
                                >
                                    <IonIcon slot="start" icon={sparklesOutline} style={{ color: "#fff" }} />
                                    Share to your story
                                </IonButton>
                            ) : (
                                <IonButton
                                    className="neon-button"
                                    expand="block"
                                    onClick={async () => await handleShare()}
                                >
                                    <IonIcon slot="start" icon={sendOutline} style={{ color: "#fff" }} />
                                    Share
                                </IonButton>
                            )}
                            {isNative && videoProperties.is_external && (
                                <p className="text-center text-xs text-zinc-400 -mt-1">
                                    Story sharing isn’t available for linked videos.
                                </p>
                            )}

                            {/* Claim Bisk — always shown; greyed/disabled when no reward */}
                            <IonButton
                                className="neon-button-highlight"
                                expand="block"
                                disabled={!claimBisk || biskDisabled}
                                onClick={async () => await handleClaimBisk()}
                            >
                                <IonIcon slot="start" color="dark" icon={biskSvg} />
                                <span>{claimBisk ? `Claim ${biskReward} Bisk` : "No Bisk to claim"}</span>
                            </IonButton>

                            {/* Bottom row — highlighted (not greyed) */}
                            <div className="grid grid-cols-3 gap-2">
                                <IonButton fill="solid" size="small" style={SECONDARY_BTN_STYLE} onClick={async () => isNative ? await handleShare() : await handleCopyLink()}>
                                    <IonIcon slot="start" icon={isNative ? sendOutline : linkOutline} />
                                    {isNative ? "Share link" : "Copy link"}
                                </IonButton>
                                <IonButton fill="solid" size="small" style={SECONDARY_BTN_STYLE} onClick={async () => await handleGoToProfile()}>
                                    <IonIcon slot="start" icon={personOutline} />
                                    View clip
                                </IonButton>
                                <IonButton fill="solid" size="small" style={SECONDARY_BTN_STYLE} onClick={async () => await uploadMore()}>
                                    <IonIcon slot="start" icon={cloudUploadOutline} />
                                    Upload more
                                </IonButton>
                            </div>

                        </div>
                    </div>
                </div>
            </IonContent>
            <StickerEditor
                isOpen={isStickerPickerOpen}
                onDismiss={() => setIsStickerPickerOpen(false)}
                tags={trick?.tags ?? []}
                sport={trick?.sport ?? ""}
                feature={trick?.terrain ?? ""}
                thumbnail={trick?.thumbnail ?? ""}
                username={state.user?.username ?? ""}
                badgeIds={state.user?.profile?.badges ?? []}
                videoUrl={trick?.video}
                trickName={trick?.trick_name}
                location={trick?.location}
            />
        </IonPage>
    );
};

export default UploadSuccess;